"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Initial Consultation",
  description: "We begin with a personal conversation to understand your vision, style, and specific needs for your wedding day or editorial project.",
  timing: "within 24 hours"
}, {
  number: "02",
  title: "Customized Trial",
  description: "A dedicated session to refine your look, ensuring every detail aligns perfectly with your features and the overall aesthetic of your event.",
  timing: "first 7 days"
}, {
  number: "03",
  title: "Booking & Planning",
  description: "We finalize the schedule, logistics, and service details, providing you with a clear roadmap for your beauty experience.",
  timing: "weeks 2 to 3"
}, {
  number: "04",
  title: "The Glow Experience",
  description: "On your special day, we provide professional, high-definition artistry, ensuring you feel radiant and confident from start to finish.",
  timing: "ongoing partnership"
}];
function TimelineStep({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className="relative transition-all duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transitionDelay: `${index * 100}ms`
  }}>
      {}
      <div className="hidden md:flex flex-col items-start">
        <div className="font-default text-[88px] leading-none text-primary tracking-tight">
          <span>{step.number.charAt(0)}</span>
          <span className="text-primary/50">{step.number.charAt(1)}</span>
        </div>

        {}
        <div className="relative w-full h-8 flex items-center mt-8 mb-8">
          <div className="absolute left-0 w-3 h-3 rounded-lg bg-primary" />
        </div>

        <h3 className="font-default text-[28px] leading-tight text-foreground mb-4">
          {step.title}
        </h3>
        <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-4">
          {step.description}
        </p>
        <p className="font-default italic text-sm text-primary">{step.timing}</p>
      </div>

      {}
      <div className="md:hidden flex gap-6 pl-2">
        <div className="flex flex-col items-center shrink-0 pt-2">
          <div className="w-3 h-3 rounded-lg bg-primary shrink-0" />
        </div>
        <div className="flex-1 pb-12">
          <div className="font-default text-[56px] leading-none text-primary tracking-tight mb-4">
            <span>{step.number.charAt(0)}</span>
            <span className="text-primary/50">{step.number.charAt(1)}</span>
          </div>
          <h3 className="font-default text-2xl leading-tight text-foreground mb-3">
            {step.title}
          </h3>
          <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-3">
            {step.description}
          </p>
          <p className="font-default italic text-sm text-primary">{step.timing}</p>
        </div>
      </div>
    </div>;
}
export default function TheGlowProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="38" id="the-glow-process" className="bg-background text-foreground py-24 md:py-[140px] px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className="max-w-3xl mb-20 md:mb-28 transition-all duration-700 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <p className="font-default text-xs tracking-widest text-primary uppercase mb-8">— OUR PROCESS</p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[64px] leading-[1.05] text-foreground tracking-tight mb-8">
            The artistry
            <br />
            <em className="text-primary font-normal italic">journey.</em>
          </h2>
          <p className="font-default text-[15px] md:text-base leading-relaxed text-muted-foreground max-w-xl">
            Every transformation begins with a vision. Here is how we bring your 
            unique beauty to life at Joanne Glow Studio.
          </p>
        </div>

        {}
        <div className="hidden md:block relative">
          {}
          <div className="absolute left-0 right-0 h-px bg-border pointer-events-none" style={{
          top: "calc(88px + 2rem + 0.625rem)"
        }} aria-hidden="true" />
          <div className="grid grid-cols-4 gap-8 lg:gap-12">
            {steps.map((step, i) => <TimelineStep key={step.number} step={step} index={i} />)}
          </div>
        </div>

        {}
        <div className="md:hidden relative">
          {}
          <div className="absolute left-3 top-0 bottom-0 w-px bg-border pointer-events-none" aria-hidden="true" />
          <div className="flex flex-col">
            {steps.map((step, i) => <TimelineStep key={step.number} step={step} index={i} />)}
          </div>
        </div>

        {}
        <div ref={footerRef} className="mt-20 md:mt-32 transition-all duration-700 ease-out" style={{
        opacity: footerVisible ? 1 : 0,
        transform: footerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <Separator className="mb-12 bg-border" />
          <div className="flex justify-center">
            <Link to="/BridalSuitePage" className="inline-flex items-center gap-2 font-default text-base text-primary group">
              <span className="underline decoration-primary decoration-1 underline-offset-[6px] group-hover:decoration-2 transition-all">
                Download our bridal booking guide
              </span>
              <ArrowRight className="size-4 text-primary transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}